import React from 'react';
import { X, User, AlertCircle, Download, MoreHorizontal } from 'lucide-react';

// Excess consumption API item shape: taskName, itemName, unit, estimated, actual, consumptionPattern, assignedEngineer
const buildModalDataFromExcess = (data) => {
    if (!data?.itemName) return null;
    const cp = data.consumptionPattern || {};
    const unit = data.unit || cp.unit || '';
    const required = cp.required ?? 0;
    const totalConsumption = cp.totalConsumption ?? data.actual ?? 0;
    const history = (cp.releaseHistory || []).map((r, i) => ({
        id: i + 1,
        title: `Release #${i + 1}`,
        date: r.date ? new Date(r.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
        value: `${r.quantity ?? 0} ${unit}`.trim()
    }));
    return {
        material: data.itemName,
        task: data.taskName || '—',
        engineer: data.assignedEngineer
            ? { name: data.assignedEngineer.name || '—', role: data.assignedEngineer.designation || '—' }
            : { name: '—', role: '—' },
        stats: [
            { label: 'Required Quantity', value: `${required} ${unit}`.trim() || '—' },
            { label: 'Total Consumption', value: `${totalConsumption} ${unit}`.trim() || '—' },
            { label: 'Estimated', value: `${data.estimated ?? 0} ${unit}`.trim() || '—' },
            { label: 'Actual', value: `${data.actual ?? 0} ${unit}`.trim(), isCritical: (data.excess ?? 0) > 0 }
        ],
        history: history.length > 0 ? history : [{ id: 1, title: 'No releases', date: '—', value: '—' }],
        analysis: data.excess > 0
            ? `Excess consumption: ${data.excess} ${unit}. Estimated ${data.estimated ?? 0} ${unit}, actual ${data.actual ?? 0} ${unit}.`
            : 'No excess consumption recorded.'
    };
};

const ConsumptionPatternModal = ({ isOpen, onClose, data }) => {
    if (!isOpen) return null;

    const fromExcess = buildModalDataFromExcess(data);
    const modalData = fromExcess || {
        material: data?.material || 'Steel Bar',
        task: 'Foundation Work',
        engineer: { name: 'Rajesh Sharma', role: 'Site Engineer' },
        stats: [
            { label: 'Required Quantity', value: data?.requiredQty || '35 Tons' },
            { label: 'Total Consumption', value: '32.8 Tons' },
            { label: 'Vendor Requests', value: '3 Times' },
            { label: 'Consumption Rate', value: '95%', isCritical: true }
        ],
        history: [
            { id: 1, title: 'Release #1', date: '05 Jan 2026', value: '15 Tons' },
            { id: 2, title: 'Release #2', date: '12 Jan 2026', value: '10 Tons' },
            { id: 3, title: 'Release #3', date: '18 Jan 2026', value: '7.8 Tons' }
        ],
        analysis: 'Consumption is at 93% against estimated 80%. Total 2800 meters consumed out of 3000 meters required.'
    };

    return (
        <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-[2px] animate-in fade-in duration-300"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white rounded-[24px] sm:rounded-[32px] md:rounded-[40px] w-full max-w-[550px] max-h-[92vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
                {/* Header - Condensed */}
                <div className="px-5 sm:px-8 py-4 border-b border-black bg-white sticky top-0 z-10">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Consumption Pattern Details</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors group"
                        >
                            <X size={20} className="text-gray-400 group-hover:text-gray-900" />
                        </button>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-base sm:text-lg font-bold text-gray-900">{modalData.material}</h3>
                            <span className="text-gray-400 text-xs hidden sm:inline">-</span>
                            <p className="text-gray-500 text-xs font-medium">Task: <span className="text-gray-800">{modalData.task}</span></p>
                        </div>
                        <div className="flex items-center gap-2.5 bg-gray-50/80 px-2.5 py-1.5 rounded-xl border border-gray-100/50">
                            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center border border-indigo-200">
                                <User size={14} className="text-indigo-600" />
                            </div>
                            <div className="text-left">
                                <p className="text-[11px] font-bold text-gray-900 leading-tight">{modalData.engineer.name}</p>
                                <p className="text-[9px] text-gray-500 font-medium">{modalData.engineer.role}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body - Scrollable */}
                <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-6 scrollbar-hide">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-8">
                        {modalData.stats.map((stat, idx) => (
                            <div
                                key={idx}
                                className={`p-4 rounded-2xl border transition-all duration-300 hover:shadow-md ${stat.isCritical ? 'bg-red-50/50 border-red-100' : 'bg-white border-gray-100 shadow-sm'}`}
                            >
                                <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-1.5 font-bold">{stat.label}</p>
                                <p className={`text-lg sm:text-xl font-black ${stat.isCritical ? 'text-red-600' : 'text-gray-900'}`}>{stat.value}</p>
                                {stat.isCritical && (
                                    <div className="mt-2 h-1 w-full bg-red-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-red-500 w-[95%] transition-all duration-1000"></div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Release History */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-gray-900 font-bold text-sm">Release History</h4>
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold">LAST 3 RELEASES</span>
                        </div>
                        <div className="space-y-3">
                            {modalData.history.map((item) => (
                                <div key={item.id} className="bg-gray-50/50 rounded-2xl p-4 flex justify-between items-center border border-gray-100/50 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-[10px] font-black text-gray-400">
                                            #{item.id}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-900">{item.title}</p>
                                            <p className="text-[10px] text-gray-500 font-medium">{item.date}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm font-black text-gray-900">{item.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Analysis Box */}
                    {/* <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-2xl p-4 sm:p-5 mb-4 flex gap-4 transition-all hover:shadow-inner">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0 border border-blue-200">
                            <AlertCircle className="text-blue-600" size={20} />
                        </div>
                        <div>
                            <h5 className="text-[#1E40AF] font-bold text-sm mb-1">AI Analysis Insight</h5>
                            <p className="text-[#1E40AF] text-[11px] sm:text-xs leading-relaxed font-medium opacity-80">
                                {modalData.analysis}
                            </p>
                        </div>
                    </div> */}
                </div>

                {/* Footer Actions - Fixed bottom */}
                {/* <div className="p-5 sm:p-8 pt-4 sm:pt-4 bg-white border-t border-gray-50">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <button
                            onClick={onClose}
                            className="order-2 sm:order-1 flex-1 py-3.5 px-6 bg-gray-50 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-100 active:scale-95 transition-all text-sm"
                        >
                            Close Details
                        </button>
                        <button className="order-1 sm:order-2 flex-[1.5] py-3.5 px-6 bg-[#6366F1] rounded-xl font-bold text-white hover:bg-[#5558e6] active:scale-95 transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-100 text-sm">
                            <Download size={16} />
                            Export Data Report
                        </button>
                    </div>
                </div> */}
            </div>
            <style dangerouslySetInnerHTML={{
                __html: `
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            ` }} />
        </div>
    );
};

export default ConsumptionPatternModal;
